import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, timeout, retry } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { GlobalConfigService } from '../global-service/global-service.service';
import { TokenService } from '../../shared/services/token/token.service';

/**
 * Interface defining the structure for logger payload
 */
export interface LoggerSchema{
  level:string,
  user_id:string,
  stack_trace:string,
  message:string,
  request_id?:string
}
/**
 * Logger Service for handling application logging
 * This service provides centralized logging functionality to send logs to backend
 */
@Injectable({
  providedIn: 'root'
})
export class LoggerService {
  private readonly tokenService = inject(TokenService);

  constructor(private readonly http: HttpClient,
    private readonly GlobalconfigService: GlobalConfigService
  ) { }

  /*
   * Core logging method with built-in error resilience
   * @param logger_payload - LoggerSchema object containing log details
   * @returns Observable<any> - HTTP response from the logging endpoint
   */
  logAPI(logger_payload: LoggerSchema): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.tokenService.getAccessToken()}`
    });
    
    return this.http.post<any>(
      `${this.GlobalconfigService.loggingBaseUrl}/frontend/logger`,
      logger_payload,
      { headers }
    ).pipe(
      timeout(5000), // 5 second timeout
      retry(2), // Retry up to 2 times
      catchError((error) => {
        // Log the logging failure to console but don't throw - return empty observable
        return of({ status: 'logging_failed', error: error.message });
      })
    );
  }

  /**
   * BULLETPROOF: Fire-and-forget error logging - GUARANTEED not to crash your app
   * @param message - Error message to log
   * @param stackTrace - Optional stack trace information
   * @param userId - Optional user ID, defaults to environment LoginUserID
   * @param debugMode - Optional debug mode override
   * @param onError - Optional callback for when logging fails (for UI feedback)
   * @param requestId - Optional request ID for traceability across frontend and backend
   */
  logError(message: string, stackTrace: string = '', userId: string = environment.LoginUserID, debugMode?: boolean, onError?: (error: any) => void, requestId?: string): void {
    try {
      // Handle null GlobalconfigService gracefully
      const isDebugMode = debugMode !== undefined ? debugMode : (this.GlobalconfigService?.debugLoggingMode || false);
      // Format message with request ID if provided
      const formattedMessage = requestId ? this.GlobalconfigService.formatLogMessageWithRequestId(message, requestId) : message;
      if(isDebugMode){
        console.log(`[DEBUG] Error during ${stackTrace}: ${formattedMessage}`);
        return;
      }
      this.logErrorSafe(formattedMessage, stackTrace, userId, requestId).subscribe({
        next: (response) => {
          // Backend returns {"message": "Log saved successfully", "log": "..."} on success
          // No action needed for successful logging
        },
        error: (error) => {
          // Fallback: try to log the logging error itself
          this.logErrorSafe(`Logging error: ${error.message || error}`, 'LoggerService.logError', userId, requestId).subscribe();
          // Call the optional error callback for UI feedback
          if (onError) {
            onError(error);
          }
        }
      });
    } catch (syncError) {
      // Last resort: if even the logger fails, we need some fallback
      // Try one final attempt to log, but don't let it crash the app
      try {
        this.logErrorSafe(`Logger service synchronous error: ${(syncError as Error)?.message || syncError}`, 'LoggerService.logError.catch', userId, requestId).subscribe({
          error: () => {
            // If this also fails, we have no choice but to fail silently
            // The app must continue running
          }
        });
      } catch {
        // Complete silence - app must continue
      }
      // Call the optional error callback for UI feedback
      if (onError) {
        onError(syncError);
      }
    }
  }

  /**
   * BULLETPROOF: Fire-and-forget warning logging
   * @param message - Warning message to log
   * @param stackTrace - Optional stack trace information
   * @param userId - Optional user ID, defaults to environment LoginUserID
   * @param debugMode - Optional debug mode override
   * @param onError - Optional callback for when logging fails (for UI feedback)
   * @param requestId - Optional request ID for traceability across frontend and backend
   */
  logWarning(message: string, stackTrace: string = '', userId: string = environment.LoginUserID, debugMode?: boolean, onError?: (error: any) => void, requestId?: string): void {
    try {
      // Handle null GlobalconfigService gracefully
      const isDebugMode = debugMode !== undefined ? debugMode : (this.GlobalconfigService?.debugLoggingMode || false);
      // Format message with request ID if provided
      const formattedMessage = requestId ? this.GlobalconfigService.formatLogMessageWithRequestId(message, requestId) : message;
      if(isDebugMode){
        console.log(`[DEBUG] Warning during ${stackTrace}: ${formattedMessage}`);
        return;
      }
      this.logWarningSafe(formattedMessage, stackTrace, userId, requestId).subscribe({
        next: (response) => {
          // Backend returns {"message": "Log saved successfully", "log": "..."} on success
          // No action needed for successful logging
        },
        error: (error) => {
          // Fallback: try to log the logging error itself
          this.logErrorSafe(`Warning logging error: ${error.message || error}`, 'LoggerService.logWarning', userId, requestId).subscribe();
          // Call the optional error callback for UI feedback
          if (onError) {
            onError(error);
          }
        }
      });
    } catch (syncError) {
      // Last resort: try one final attempt to log the error
      try {
        this.logErrorSafe(`Logger service synchronous error in logWarning: ${(syncError as Error)?.message || syncError}`, 'LoggerService.logWarning.catch', userId, requestId).subscribe({
          error: () => {
            // If this also fails, we have no choice but to fail silently
          }
        });
      } catch {
        // Complete silence - app must continue
      }
      // Call the optional error callback for UI feedback
      if (onError) {
        onError(syncError);
      }
    }
  }

  /**
   * BULLETPROOF: Fire-and-forget info logging
   * @param message - Info message to log
   * @param stackTrace - Optional stack trace information
   * @param userId - Optional user ID, defaults to environment LoginUserID
   * @param debugMode - Optional debug mode override
   * @param onError - Optional callback for when logging fails (for UI feedback)
   * @param requestId - Optional request ID for traceability across frontend and backend
   */
  logInfo(message: string, stackTrace: string = '', userId: string = environment.LoginUserID, debugMode?: boolean, onError?: (error: any) => void, requestId?: string): void {
    try {
      // Handle null GlobalconfigService gracefully
      const isDebugMode = debugMode !== undefined ? debugMode : (this.GlobalconfigService?.debugLoggingMode || false);
      // Format message with request ID if provided
      const formattedMessage = requestId ? this.GlobalconfigService.formatLogMessageWithRequestId(message, requestId) : message;
      if(isDebugMode){
        console.log(`[DEBUG] Info during ${stackTrace}: ${formattedMessage}`);
        return;
      }
      this.logInfoSafe(formattedMessage, stackTrace, userId, requestId).subscribe({
        next: (response) => {
          // Backend returns {"message": "Log saved successfully", "log": "..."} on success
          // No action needed for successful logging
        },
        error: (error) => {
          // Fallback: try to log the logging error itself
          this.logErrorSafe(`Info logging error: ${error.message || error}`, 'LoggerService.logInfo', userId, requestId).subscribe();
          // Call the optional error callback for UI feedback
          if (onError) {
            onError(error);
          }
        }
      });
    } catch (syncError) {
      // Last resort: try one final attempt to log the error
      try {
        this.logErrorSafe(`Logger service synchronous error in logInfo: ${(syncError as Error)?.message || syncError}`, 'LoggerService.logInfo.catch', userId, requestId).subscribe({
          error: () => {
            // If this also fails, we have no choice but to fail silently
          }
        });
      } catch {
        // Complete silence - app must continue
      }
      // Call the optional error callback for UI feedback
      if (onError) {
        onError(syncError);
      }
    }
  }

  /**
   * Convenience method for logging errors
   * @param message - Error message to log
   * @param stackTrace - Optional stack trace information
   * @param userId - Optional user ID, defaults to environment LoginUserID
   * @param requestId - Optional request ID for traceability across frontend and backend
   */
  logErrorSafe(message: string, stackTrace: string = '', userId: string = environment.LoginUserID, requestId?: string): Observable<any> {
    return this.logAPI({
      level: 'ERROR',
      stack_trace: stackTrace,
      user_id: userId,
      message: message,
      request_id: requestId
    });
  }

  /**
   * Convenience method for logging warnings
   * @param message - Warning message to log
   * @param stackTrace - Optional stack trace information
   * @param userId - Optional user ID, defaults to environment LoginUserID
   * @param requestId - Optional request ID for traceability across frontend and backend
   */
  logWarningSafe(message: string, stackTrace: string = '', userId: string = environment.LoginUserID, requestId?: string): Observable<any> {
    return this.logAPI({
      level: 'WARNING',
      stack_trace: stackTrace,
      user_id: userId,
      message: message,
      request_id: requestId
    });
  }

  /**
   * Convenience method for logging info messages
   * @param message - Info message to log
   * @param stackTrace - Optional stack trace information
   * @param userId - Optional user ID, defaults to environment LoginUserID
   * @param requestId - Optional request ID for traceability across frontend and backend
   */
  logInfoSafe(message: string, stackTrace: string = '', userId: string = environment.LoginUserID, requestId?: string): Observable<any> {
    return this.logAPI({
      level: 'INFO',
      stack_trace: stackTrace,
      user_id: userId,
      message: message,
      request_id: requestId
    });
  }
}
